import React, { useContext, useEffect, useMemo, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import ThinFooter from "../Footer/ThinFooter";
import ContentWrapper from "../Atomics/ContentWrapper/ContentWrapper";
import FloorMap from "./FloorMap";
import { toPersianDigits } from "../../utils/exhibitionUtils";
import { ExhibitionContext } from "../../context/exhibition";
import { exhibitionFetcher } from "../../api/requests";
import DatePicker from "react-multi-date-picker";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { useToaster } from "../../context/toaster";

const Admin = () => {
  const [floors, setFloors] = useState([]);
  // Draft stores per floor (for create preview before saving)
  const [storeDrafts, setStoreDrafts] = useState({}); // { [floorId]: Array<StoreDraft> }
  // Cover preview URLs for draft stores
  const [coverPreviews, setCoverPreviews] = useState({}); // { [storeId]: previewURL }
  const [idx, setIdx] = useState(0);
  const [query, setQuery] = useState("");
  const [siteTheme, setSiteTheme] = useState("#cae0da");
  const { exhibitions, updateExhibitions, getExhibitions } =
    useContext(ExhibitionContext);
  const { addMessage } = useToaster();

  // Exhibition and floor selection state
  const [selectedExhibitionId, setSelectedExhibitionId] = useState(null);
  const [exhibitionFloors, setExhibitionFloors] = useState([]);
  const [selectedFloorId, setSelectedFloorId] = useState(null);
  const [isLoadingFloors, setIsLoadingFloors] = useState(false);

  // Stores state
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [deletingStoreId, setDeletingStoreId] = useState(null);

  // Meeting cards state
  const [meetingCards, setMeetingCards] = useState([]);
  const [isLoadingMeetingCards, setIsLoadingMeetingCards] = useState(false);

  // Exhibition creation form state
  const [showExhibitionForm, setShowExhibitionForm] = useState(false);
  const [exhibitionForm, setExhibitionForm] = useState({
    exhibition_name: "",
    exhibition_year: "",
    exhibition_place: "",
    created_date: new DateObject({ calendar: persian, locale: persian_fa }),
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Floor creation form state
  const [showFloorForm, setShowFloorForm] = useState(false);
  const [isSubmittingFloor, setIsSubmittingFloor] = useState(false);
  const [floorForm, setFloorForm] = useState({
    floor_name: "",
    floor_number: "",
    floor_background_color: siteTheme,
    entrance_x: 0,
    entrance_y: 0,
    entrance_position: "top",
    created_date: new DateObject({ calendar: persian, locale: persian_fa }),
  });
  
  // Mobile responsive state - no longer needed for sidebar toggle

  // Store creation/editing state (sidebar-driven)
  const [isSubmittingStore, setIsSubmittingStore] = useState(false);

  const currentFloor = exhibitionFloors.find(
    (floor) => floor.id === Number(selectedFloorId)
  );
  const current = currentFloor
    ? {
        ...currentFloor,
        entrance_x: currentFloor.entrance_x || 50,
        entrance_y: currentFloor.entrance_y || 50,
        entrance_position: currentFloor.entrance_position || "top",
      }
    : floors[idx];

  // Use local stores when a floor is selected to ensure newly added placeholders appear immediately
  const effectiveStores = useMemo(() => {
    const backendStores = stores || [];
    const drafts = storeDrafts[String(selectedFloorId)] || [];
    const normalizedBackend = (backendStores || []).map((v) => ({
      ...v,
      start_x: Number(typeof v.start_x !== "undefined" ? v.start_x : v.x ?? 0),
      start_y: Number(typeof v.start_y !== "undefined" ? v.start_y : v.y ?? 0),
      width: Number(v.width ?? 0),
      height: Number(v.height ?? 0),
    }));
    return [...normalizedBackend, ...drafts];
  }, [stores, storeDrafts, selectedFloorId]);

  useEffect(() => {
    const savedTheme = localStorage.getItem("exhibition_site_theme");
    const initial =
      savedTheme ||
      (typeof window !== "undefined"
        ? getComputedStyle(document.documentElement)
            .getPropertyValue("--site-theme")
            .trim() || "#cae0da"
        : "#cae0da");
    setSiteTheme(initial);
    if (savedTheme && typeof window !== "undefined") {
      document.documentElement.style.setProperty("--site-theme", savedTheme);
    }
    // Fetch meeting cards on component mount
    fetchMeetingCards();
  }, []);

  // Sync theme with selected floor background color
  useEffect(() => {
    const color = current?.floor_background_color;
    if (color) {
      onChangeTheme(color);
    }
  }, [current?.id, current?.floor_background_color]);

  // Fetch stores when floor is selected
  useEffect(() => {
    if (selectedFloorId) {
      setStores([]); // Clear previous stores
      fetchFloorStores(selectedFloorId);
    } else {
      setStores([]); // Clear stores when no floor is selected
    }
  }, [selectedFloorId]);

  // Cleanup cover preview URLs on component unmount
  useEffect(() => {
    return () => {
      Object.values(coverPreviews).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, []);


  const onChangeTheme = (value) => {
    setSiteTheme(value);
    if (typeof window !== "undefined") {
      document.documentElement.style.setProperty("--site-theme", value);
    }
    localStorage.setItem("exhibition_site_theme", value);
  };

  const updateFloors = (next) => {
    setFloors(next);
  };

  const addFloor = () => {
    addMessage({
      id: Math.random().toString(36).substr(2, 9),
      type: "error",
      text: "افزودن سالن تنها از طریق فرم ایجاد سالن پشتیبانی می‌شود.",
    });
  };

  const addExhibition = async () => {
    // Validate form
    if (!exhibitionForm.exhibition_name.trim()) {
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "نام نمایشگاه الزامی است.",
      });
      return;
    }

    if (!exhibitionForm.exhibition_year.trim()) {
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "سال و ماه نمایشگاه الزامی است.",
      });
      return;
    }

    if (!exhibitionForm.exhibition_place.trim()) {
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "مکان نمایشگاه الزامی است.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const exhibitionData = {
        exhibition_name: exhibitionForm.exhibition_name.trim(),
        exhibition_year: exhibitionForm.exhibition_year.trim(),
        exhibition_place: exhibitionForm.exhibition_place.trim(),
        created_date: exhibitionForm.created_date.format(
          "YYYY-MM-DDTHH:mm:ss.SSSZ"
        ),
      };

      const response = await exhibitionFetcher.postExhibition(exhibitionData);

      if (response) {
        addMessage({
          id: Math.random().toString(36).substr(2, 9),
          type: "success",
          text: "نمایشگاه با موفقیت ایجاد شد.",
        });

        // Reset form
        setExhibitionForm({
          exhibition_name: "",
          exhibition_year: "",
          exhibition_place: "",
          created_date: new DateObject({
            calendar: persian,
            locale: persian_fa,
          }),
        });
        setShowExhibitionForm(false);

        // Refresh exhibitions list
        if (getExhibitions) {
          await getExhibitions();
        }
      }
    } catch (error) {
      console.error("Error creating exhibition:", error);
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "خطا در ایجاد نمایشگاه. لطفا دوباره تلاش کنید.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExhibitionFormChange = (field, value) => {
    setExhibitionForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFloorFormChange = (field, value) => {
    setFloorForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Removed central store form handler (sidebar will manage per-store fields)

  const openFloorModal = () => {
    // Prefill from current controls
    const entranceX = current?.entrance?.x ?? 0;
    const entranceY = current?.entrance?.y ?? 0;
    const entrancePos = current?.entrance?.position || "top";
    setFloorForm({
      floor_name: "",
      floor_number: "",
      floor_background_color: siteTheme,
      entrance_x: entranceX,
      entrance_y: entranceY,
      entrance_position: entrancePos,
      created_date: new DateObject({ calendar: persian, locale: persian_fa }),
    });
    setShowFloorForm(true);
  };

  const submitFloor = async () => {
    if (!selectedExhibitionId) {
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "لطفاً ابتدا نمایشگاه را انتخاب کنید.",
      });
      return;
    }
    if (!floorForm.floor_name.trim()) {
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "نام سالن الزامی است.",
      });
      return;
    }
    if (!floorForm.floor_number.toString().trim()) {
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "شماره سالن الزامی است.",
      });
      return;
    }

    setIsSubmittingFloor(true);
    try {
      const payload = {
        exhibition: Number(selectedExhibitionId),
        floor_name: floorForm.floor_name.trim(),
        floor_number: floorForm.floor_number,
        floor_background_color: floorForm.floor_background_color,
        entrance_x: Number(floorForm.entrance_x) || 0,
        entrance_y: Number(floorForm.entrance_y) || 0,
        entrance_position: floorForm.entrance_position,
        created_date: floorForm.created_date.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
      };

      const res = await exhibitionFetcher.postFloor(payload);
      if (res) {
        addMessage({
          id: Math.random().toString(36).substr(2, 9),
          type: "success",
          text: "سالن با موفقیت ایجاد شد.",
        });
        setShowFloorForm(false);
        await fetchExhibitionFloors(selectedExhibitionId);
      }
    } catch (error) {
      console.error("Error creating floor:", error);
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "خطا در ایجاد سالن. لطفا دوباره تلاش کنید.",
      });
    } finally {
      setIsSubmittingFloor(false);
    }
  };

  const updateFloor = async () => {
    if (!current || !current.id) {
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "لطفاً ابتدا یک سالن انتخاب کنید.",
      });
      return;
    }

    try {
      const payload = {
        floor_name: current.floor_name,
        floor_number: current.floor_number,
        floor_background_color: current.floor_background_color,
        entrance_x: current.entrance_x,
        entrance_y: current.entrance_y,
        entrance_position: current.entrance_position,
        created_date: current.created_date,
        exhibition: Number(selectedExhibitionId),
      };

      await exhibitionFetcher.updateFloor(current.id, payload);
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "success",
        text: "سالن با موفقیت به‌روزرسانی شد.",
      });

      // Refresh floors for current exhibition
      if (selectedExhibitionId) {
        await fetchExhibitionFloors(selectedExhibitionId);
      }
    } catch (error) {
      console.error("Error updating floor:", error);
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "خطا در به‌روزرسانی سالن. لطفاً دوباره تلاش کنید.",
      });
    }
  };

  // Store APIs
  const submitCreateStore = async (s) => {
    if (!selectedFloorId) {
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "لطفاً ابتدا سالن را انتخاب کنید.",
      });
      return;
    }
    if (!s?.store_name?.trim()) {
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "نام غرفه الزامی است.",
      });
      return;
    }

    setIsSubmittingStore(true);
    try {
      let res;
      if (s.cover) {
        const formData = new FormData();
        formData.append("floor", Number(selectedFloorId));
        if (s.store_id_number != null)
          formData.append("store_id_number", Number(s.store_id_number));
        formData.append("store_name", s.store_name.trim());
        formData.append("cover", s.cover);
        formData.append("start_x", Number(s.start_x) || 0);
        formData.append("start_y", Number(s.start_y) || 0);
        formData.append("width", Number(s.width) || 0);
        formData.append("height", Number(s.height) || 0);
        formData.append("entrance", s.entrance || "bottom");
        formData.append("description", s.description || "");
        formData.append("products", s.products || "");
        formData.append("phone_number", s.phone_number || "");
        formData.append(
          "vaghfnama_channel_address",
          s.vaghfnama_channel_address || ""
        );
        formData.append("site_address", s.site_address || "");
        formData.append(
          "created_date",
          s.created_date
            ? s.created_date.format
              ? s.created_date.format("YYYY-MM-DDTHH:mm:ss.SSSZ")
              : s.created_date
            : ""
        );
        res = await exhibitionFetcher.postStore(formData);
      } else {
        const payload = {
          floor: Number(selectedFloorId),
          store_id_number:
            s.store_id_number != null ? Number(s.store_id_number) : undefined,
          store_name: s.store_name.trim(),
          start_x: Number(s.start_x) || 0,
          start_y: Number(s.start_y) || 0,
          width: Number(s.width) || 0,
          height: Number(s.height) || 0,
          entrance: s.entrance || "bottom",
          description: s.description || "",
          products: s.products || "",
          phone_number: s.phone_number || "",
          vaghfnama_channel_address: s.vaghfnama_channel_address || "",
          site_address: s.site_address || "",
          created_date: s.created_date
            ? s.created_date.format
              ? s.created_date.format("YYYY-MM-DDTHH:mm:ss.SSSZ")
              : s.created_date
            : "",
        };
        res = await exhibitionFetcher.postStore(payload);
      }
      if (res) {
        addMessage({
          id: Math.random().toString(36).substr(2, 9),
          type: "success",
          text: "غرفه با موفقیت ایجاد شد.",
        });
        // Optionally refresh floor data to get updated stores if backend returns them
        if (selectedExhibitionId) {
          await fetchExhibitionFloors(selectedExhibitionId);
        }
      }
    } catch (error) {
      console.error("Error creating store:", error);
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "خطا در ایجاد غرفه. لطفاً دوباره تلاش کنید.",
      });
    } finally {
      setIsSubmittingStore(false);
    }
  };

  const submitUpdateStoreBody = async (s) => {
    const storeId = Number(s?.id);
    if (!storeId) {
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "شناسه غرفه نامعتبر است.",
      });
      return;
    }
    try {
      const payload = {
        floor: Number(selectedFloorId) || undefined,
        store_id_number:
          s.store_id_number != null ? Number(s.store_id_number) : undefined,
        store_name: s.store_name?.trim() || undefined,
        start_x: Number(s.start_x) || 0,
        start_y: Number(s.start_y) || 0,
        width: Number(s.width) || 0,
        height: Number(s.height) || 0,
        entrance: s.entrance || "bottom",
        description: s.description || "",
        products: s.products || "",
        phone_number: s.phone_number || "",
        vaghfnama_channel_address: s.vaghfnama_channel_address || "",
        site_address: s.site_address || "",
        created_date: s.created_date || undefined,
      };

      await exhibitionFetcher.updateStoreBody(storeId, payload);
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "success",
        text: "اطلاعات غرفه با موفقیت به‌روزرسانی شد.",
      });
      if (selectedExhibitionId) {
        await fetchExhibitionFloors(selectedExhibitionId);
      }
    } catch (error) {
      console.error("Error updating store body:", error);
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "خطا در به‌روزرسانی اطلاعات غرفه.",
      });
      throw error; // Re-throw to let the button handler know there was an error
    }
  };

  const submitUpdateStoreCover = async (s) => {
    const storeId = Number(s?.id);
    if (!storeId) {
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "شناسه غرفه نامعتبر است.",
      });
      return;
    }
    if (!s.cover) {
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "تصویر کاور را انتخاب کنید.",
      });
      return;
    }
    try {
      const formData = new FormData();
      formData.append("cover", s.cover);
      await exhibitionFetcher.updateStoreCover(storeId, formData);
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "success",
        text: "کاور غرفه به‌روزرسانی شد.",
      });
      if (selectedExhibitionId) {
        await fetchExhibitionFloors(selectedExhibitionId);
      }
    } catch (error) {
      console.error("Error updating store cover:", error);
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "خطا در به‌روزرسانی کاور غرفه.",
      });
      throw error; // Re-throw to let the button handler know there was an error
    }
  };

  const submitDeleteStore = async (storeId) => {
    if (!storeId) {
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "شناسه غرفه نامعتبر است.",
      });
      return;
    }

    setDeletingStoreId(storeId);
    try {
      await exhibitionFetcher.deleteStore(storeId);
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "success",
        text: "غرفه با موفقیت حذف شد.",
      });

      // Refresh the stores list
      if (selectedFloorId) {
        await fetchFloorStores(selectedFloorId);
      }
    } catch (error) {
      console.error("Error deleting store:", error);
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "خطا در حذف غرفه.",
      });
    } finally {
      setDeletingStoreId(null);
    }
  };

  const fetchExhibitionFloors = async (exhibitionId) => {
    if (!exhibitionId) {
      setExhibitionFloors([]);
      setSelectedFloorId(null);
      return;
    }

    setIsLoadingFloors(true);
    try {
      const floors = await exhibitionFetcher.getExhibitionFloors(
        Number(exhibitionId)
      );
      const fetchedFloors = floors?.results || floors || [];
      // Filter out floors with empty names
      const filteredFloors = fetchedFloors.filter(
        (floor) => floor.floor_name && floor.floor_name.trim() !== ""
      );
      setExhibitionFloors(filteredFloors);

      // Reset floor selection when exhibition changes
      setSelectedFloorId(null);
      setIdx(0);
    } catch (error) {
      console.error("Error fetching exhibition floors:", error);
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "خطا در دریافت سالن‌های نمایشگاه.",
      });
      setExhibitionFloors([]);
    } finally {
      setIsLoadingFloors(false);
    }
  };

  const fetchFloorStores = async (floorId) => {
    if (!floorId) {
      setStores([]);
      return;
    }

    setStoresLoading(true);
    try {
      const res = await exhibitionFetcher.getFloorStores(floorId);
      setStores(res?.results || []);
    } catch (error) {
      console.error("Error fetching floor stores:", error);
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "خطا در دریافت غرفه‌های سالن.",
      });
      setStores([]);
    } finally {
      setStoresLoading(false);
    }
  };

  const fetchMeetingCards = async () => {
    setIsLoadingMeetingCards(true);
    try {
      const meetings = await exhibitionFetcher.getMeetingCards();
      const fetchedMeetings = meetings?.results || meetings || [];
      setMeetingCards(fetchedMeetings);
    } catch (error) {
      console.error("Error fetching meeting cards:", error);
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "خطا در دریافت کارت‌های جلسات.",
      });
      setMeetingCards([]);
    } finally {
      setIsLoadingMeetingCards(false);
    }
  };

  const handleExhibitionChange = (exhibitionId) => {
    const numericId = Number(exhibitionId) || null;
    setSelectedExhibitionId(numericId);
    fetchExhibitionFloors(numericId);
  };

  const addStore = async () => {
    if (!selectedExhibitionId) {
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "لطفاً ابتدا نمایشگاه را انتخاب کنید.",
      });
      return;
    }
    if (!selectedFloorId) {
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "لطفاً ابتدا سالن را انتخاب کنید.",
      });
      return;
    }
    // Create a local draft to preview on map and edit in sidebar
    const existingIds = (stores || [])
      .map((s) => Number(s.store_id_number ?? s.id))
      .filter((n) => !Number.isNaN(n));
    const nextNumericId =
      (existingIds.length ? Math.max(...existingIds) : 0) + 1;
    const draftId = `draft-${Date.now()}`;
    setStoreDrafts((prev) => {
      const key = String(selectedFloorId);
      const list = prev[key] || [];
      return {
        ...prev,
        [key]: [
          ...list,
          {
            id: draftId,
            __draft: true,
            store_id_number: nextNumericId,
            store_name: "",
            start_x: 50,
            start_y: 50,
            width: 100,
            height: 60,
            entrance: "bottom",
            description: "",
            products: "",
            phone_number: "",
            vaghfnama_channel_address: "",
            site_address: "",
            created_date: new DateObject({
              calendar: persian,
              locale: persian_fa,
            }),
          },
        ],
      };
    });
  };

  const updateDraftStore = (storeId, patch) => {
    const floorKey = String(selectedFloorId);
    setStoreDrafts((prev) => {
      const list = (prev[floorKey] || []).map((item) =>
        item.id === storeId ? { ...item, ...patch } : item
      );
      return { ...prev, [floorKey]: list };
    });
  };

  const removeDraftStore = (storeId) => {
    const floorKey = String(selectedFloorId);
    setStoreDrafts((prev) => {
      const list = (prev[floorKey] || []).filter((item) => item.id !== storeId);
      return { ...prev, [floorKey]: list };
    });
    // Clean up cover preview URL
    setCoverPreviews((prev) => {
      const newPreviews = { ...prev };
      if (newPreviews[storeId]) {
        URL.revokeObjectURL(newPreviews[storeId]);
        delete newPreviews[storeId];
      }
      return newPreviews;
    });
  };

  // Handle cover file selection and create preview
  const handleCoverChange = (storeId, file) => {
    // Clean up previous preview URL for this store
    setCoverPreviews((prev) => {
      if (prev[storeId]) {
        URL.revokeObjectURL(prev[storeId]);
      }
      return { ...prev, [storeId]: file ? URL.createObjectURL(file) : null };
    });
  };

  const updateStore = (si, patch) => {
    const store = effectiveStores?.[si];
    if (!store) return;
    if (store.__draft) {
      updateDraftStore(store.id, patch);
      return;
    }
    // If it's a backend store, update the stores state directly
    setStores((prevStores) =>
      prevStores.map((s) => (s.id === store.id ? { ...s, ...patch } : s))
    );
  };

  const removeStore = (si) => {
    const next = floors.map((f, i) =>
      i === idx ? { ...f, stores: f.stores.filter((_, j) => j !== si) } : f
    );
    updateFloors(next);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = (effectiveStores || []).map((s, si) => ({ s, si }));
    if (!q) return list;
    return list.filter(({ s }) => {
      const idStr = String(s.id || "");
      const storeIdNumber = String(s.store_id_number || "");
      const booth = String(s.boothNumber || "");
      const name = String(s.store_name || "").toLowerCase();
      return (
        idStr.includes(q) ||
        storeIdNumber.includes(q) ||
        booth.toLowerCase().includes(q) ||
        name.includes(q)
      );
    });
  }, [effectiveStores, query]);

  const saveDraftStore = async (draft) => {
    if (!selectedFloorId) return;
    if (!draft.store_name || !String(draft.store_name).trim()) {
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "نام غرفه الزامی است.",
      });
      return;
    }
    // store_id_number is optional - only validate if provided
    if (
      draft.store_id_number !== undefined &&
      draft.store_id_number !== null &&
      draft.store_id_number !== "" &&
      Number.isNaN(Number(draft.store_id_number))
    ) {
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: "شماره غرفه (store_id_number) باید عددی باشد.",
      });
      return;
    }
    setIsSubmittingStore(true);
    try {
      let res;
      if (draft.cover) {
        const formData = new FormData();
        formData.append("store_id_number", draft.store_id_number);
        formData.append("floor", Number(selectedFloorId));
        formData.append("store_name", (draft.store_name || "").trim());
        formData.append("cover", draft.cover);
        formData.append("start_x", Number(draft.start_x) || 0);
        formData.append("start_y", Number(draft.start_y) || 0);
        formData.append("width", Number(draft.width) || 0);
        formData.append("height", Number(draft.height) || 0);
        formData.append("entrance", draft.entrance || "bottom");
        formData.append("description", draft.description || "");
        formData.append("products", draft.products || "");
        formData.append("phone_number", draft.phone_number || "");
        formData.append(
          "vaghfnama_channel_address",
          draft.vaghfnama_channel_address || ""
        );
        formData.append("site_address", draft.site_address || "");
        formData.append(
          "created_date",
          draft.created_date
            ? draft.created_date.format
              ? draft.created_date.format("YYYY-MM-DDTHH:mm:ss.SSSZ")
              : draft.created_date
            : ""
        );
        res = await exhibitionFetcher.postStore(formData);
      } else {
        const payload = {
          floor: Number(selectedFloorId),
          ...(draft.store_id_number != null &&
            !isNaN(Number(draft.store_id_number)) && {
              store_id_number: Number(draft.store_id_number),
            }),
          store_name: (draft.store_name || "").trim(),
          start_x: Number(draft.start_x) || 0,
          start_y: Number(draft.start_y) || 0,
          width: Number(draft.width) || 0,
          height: Number(draft.height) || 0,
          entrance: draft.entrance || "bottom",
          description: draft.description || "",
          products: draft.products || "",
          phone_number: draft.phone_number || "",
          vaghfnama_channel_address: draft.vaghfnama_channel_address || "",
          site_address: draft.site_address || "",
          created_date: draft.created_date
            ? draft.created_date.format
              ? draft.created_date.format("YYYY-MM-DDTHH:mm:ss.SSSZ")
              : draft.created_date
            : "",
        };
        res = await exhibitionFetcher.postStore(payload);
      }

      if (res) {
        addMessage({
          id: Math.random().toString(36).substr(2, 9),
          type: "success",
          text: "غرفه ایجاد شد.",
        });
        removeDraftStore(draft.id);
        await fetchExhibitionFloors(selectedExhibitionId);
      }
    } catch (e) {
      console.error("Store creation error:", e);
      console.error("Error response:", e.response?.data);
      console.error("Error status:", e.response?.status);
      console.error("Error config:", e.config);
      console.error(
        "Full error object:",
        JSON.stringify(e.response?.data, null, 2)
      );
      addMessage({
        id: Math.random().toString(36).substr(2, 9),
        type: "error",
        text: `خطا در ایجاد غرفه: ${
          e.response?.data?.message ||
          e.response?.data?.detail ||
          e.message ||
          "خطای نامشخص"
        }`,
      });
    } finally {
      setIsSubmittingStore(false);
    }
  };

  return (
    <>
      <ContentWrapper>
        <Container fluid>
          <section className="blog-page section-padding">
            <Container>
              <Row style={{ minHeight: "85vh" }}>
                <div
                  className="exhibition-container"
                  style={{ padding: 16, width: "100%" }}
                >
                  <div
                    className="exhibition-row"
                    style={{
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 12,
                    }}
                  >
                    <h1 className="exhibition-title" style={{ margin: 0 }}>
                      مدیریت نقشه
                    </h1>
                  </div>

                  <div
                    className="exhibition-grid exhibition-admin-grid"
                    style={{
                      gap: 16,
                      textAlign: "right",
                    }}
                  >
                    <div className="exhibition-card">
                      <div
                        className="exhibition-row"
                        style={{
                          gap: 8,
                          alignItems: "center",
                          marginBottom: 12,
                        }}
                      >
                        <div className="exhibition-label">انتخاب نمایشگاه:</div>
                        <select
                          className="exhibition-select"
                          value={selectedExhibitionId ?? ""}
                          onChange={(e) =>
                            handleExhibitionChange(e.target.value)
                          }
                        >
                          <option value="">انتخاب نمایشگاه</option>
                          {exhibitions?.results
                            ?.filter((item) => item.exhibition_name?.trim())
                            .map((exhibition) => (
                              <option key={exhibition.id} value={exhibition.id}>
                                {exhibition.exhibition_name?.trim()}
                              </option>
                            ))}
                        </select>
                        <button
                          className="exhibition-btn"
                          style={{ marginRight: "auto" }}
                          onClick={() => setShowExhibitionForm(true)}
                        >
                          افزودن نمایشگاه
                        </button>
                      </div>

                      <div
                        className="exhibition-row"
                        style={{
                          gap: 8,
                          alignItems: "center",
                          marginBottom: 12,
                        }}
                      >
                        <div className="exhibition-label">انتخاب سالن:</div>
                        <select
                          className="exhibition-select"
                          value={selectedFloorId ?? ""}
                          onChange={(e) => {
                            const floorId = Number(e.target.value) || null;
                            setSelectedFloorId(floorId);
                            // Find the index of the selected floor in the exhibitionFloors array
                            const floorIndex = exhibitionFloors.findIndex(
                              (floor) => floor.id === floorId
                            );
                            setIdx(floorIndex >= 0 ? floorIndex : 0);
                          }}
                          disabled={!selectedExhibitionId || isLoadingFloors}
                        >
                          <option value="">
                            {isLoadingFloors
                              ? "در حال بارگذاری..."
                              : "انتخاب سالن"}
                          </option>
                          {exhibitionFloors.map((floor) => (
                            <option key={floor.id} value={floor.id}>
                              {floor.floor_name ||
                                floor.name ||
                                `سالن ${floor.id}`}
                            </option>
                          ))}
                        </select>
                        <button
                          className="exhibition-btn"
                          style={{ marginRight: "8px" }}
                          onClick={() => setShowFloorForm(!showFloorForm)}
                        >
                          {showFloorForm ? "بستن فرم" : "افزودن سالن"}
                        </button>
                        <button
                          className="exhibition-btn"
                          style={{ marginRight: "auto" }}
                          onClick={addStore}
                        >
                          افزودن غرفه
                        </button>
                      </div>

                      {/* Floor Creation Form */}
                      {showFloorForm && (
                        <div
                          className="exhibition-card"
                          style={{ marginBottom: 12 }}
                        >
                          <div
                            className="exhibition-title"
                            style={{ marginBottom: 12 }}
                          >
                            ایجاد سالن جدید
                          </div>

                          <div
                            className="exhibition-grid exhibition-grid-2"
                            style={{ marginBottom: 12 }}
                          >
                            <label className="exhibition-stack">
                              <span className="exhibition-label">نام سالن</span>
                              <input
                                className="exhibition-input"
                                type="text"
                                value={floorForm.floor_name}
                                onChange={(e) =>
                                  handleFloorFormChange(
                                    "floor_name",
                                    e.target.value
                                  )
                                }
                                placeholder="مثلاً سالن ۱"
                              />
                            </label>

                            <label className="exhibition-stack">
                              <span className="exhibition-label">
                                شماره سالن
                              </span>
                              <input
                                className="exhibition-input"
                                type="number"
                                value={floorForm.floor_number}
                                onChange={(e) =>
                                  handleFloorFormChange(
                                    "floor_number",
                                    e.target.value
                                  )
                                }
                                placeholder="مثلاً 1"
                              />
                            </label>

                            <label className="exhibition-stack">
                              <span className="exhibition-label">
                                رنگ پس‌زمینه سالن
                              </span>
                              <input
                                className="exhibition-input"
                                type="color"
                                value={floorForm.floor_background_color}
                                onChange={(e) =>
                                  handleFloorFormChange(
                                    "floor_background_color",
                                    e.target.value
                                  )
                                }
                                style={{ width: 60, height: 40, padding: 0 }}
                              />
                            </label>

                            <label className="exhibition-stack">
                              <span className="exhibition-label">
                                X ورودی سالن
                              </span>
                              <input
                                className="exhibition-input"
                                type="number"
                                value={floorForm.entrance_x}
                                onChange={(e) =>
                                  handleFloorFormChange(
                                    "entrance_x",
                                    e.target.value
                                  )
                                }
                              />
                            </label>

                            <label className="exhibition-stack">
                              <span className="exhibition-label">
                                Y ورودی سالن
                              </span>
                              <input
                                className="exhibition-input"
                                type="number"
                                value={floorForm.entrance_y}
                                onChange={(e) =>
                                  handleFloorFormChange(
                                    "entrance_y",
                                    e.target.value
                                  )
                                }
                              />
                            </label>

                            <label className="exhibition-stack">
                              <span className="exhibition-label">
                                سمت ورودی سالن
                              </span>
                              <select
                                className="exhibition-select"
                                value={floorForm.entrance_position}
                                onChange={(e) =>
                                  handleFloorFormChange(
                                    "entrance_position",
                                    e.target.value
                                  )
                                }
                              >
                                <option value="top">بالا</option>
                                <option value="bottom">پایین</option>
                                <option value="left">چپ</option>
                                <option value="right">راست</option>
                              </select>
                            </label>

                            <label className="exhibition-stack">
                              <span className="exhibition-label">
                                تاریخ ایجاد
                              </span>
                              <DatePicker
                                value={floorForm.created_date}
                                onChange={(date) =>
                                  handleFloorFormChange("created_date", date)
                                }
                                calendar={persian}
                                locale={persian_fa}
                                format="YYYY/MM/DD"
                                style={{
                                  border: "1px solid #ccc",
                                  borderRadius: "5px",
                                  padding: "8px",
                                  width: "100%",
                                }}
                              />
                            </label>
                          </div>

                          <div
                            className="exhibition-row"
                            style={{ gap: 8, justifyContent: "flex-end" }}
                          >
                            <button
                              className="exhibition-btn"
                              style={{
                                background: "#fee2e2",
                                color: "#991b1b",
                              }}
                              onClick={() => setShowFloorForm(false)}
                              disabled={isSubmittingFloor}
                            >
                              انصراف
                            </button>
                            <button
                              className="exhibition-btn"
                              onClick={submitFloor}
                              disabled={isSubmittingFloor}
                            >
                              {isSubmittingFloor
                                ? "در حال ایجاد..."
                                : "ایجاد سالن"}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Store Creation / Edit Form - moved to sidebar controls */}

                      {/* Real-time Floor Controls */}
                      {current && (
                        <div
                          style={{
                            marginBottom: 8,
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            flexWrap: "wrap",
                            padding: "8px",
                            backgroundColor: "#f8f9fa",
                            borderRadius: "6px",
                            border: "1px solid #e9ecef",
                          }}
                        >
                          <label style={{ margin: 0, minWidth: "120px" }}>
                            <span
                              style={{
                                fontSize: "11px",
                                fontWeight: "500",
                                color: "#6c757d",
                                marginBottom: "2px",
                                display: "block",
                              }}
                            >
                              نام سالن
                            </span>
                            <input
                              className="exhibition-input"
                              type="text"
                              value={current.floor_name || ""}
                              onChange={(e) => {
                                const name = e.target.value;
                                const updatedFloor = {
                                  ...current,
                                  floor_name: name,
                                };
                                setExhibitionFloors((prev) =>
                                  prev.map((floor) =>
                                    floor.id === current.id
                                      ? updatedFloor
                                      : floor
                                  )
                                );
                              }}
                              style={{
                                width: "100px",
                                height: "28px",
                                fontSize: "12px",
                                padding: "4px 6px",
                                border: "1px solid #ced4da",
                                borderRadius: "4px",
                              }}
                            />
                          </label>
                          <label style={{ margin: 0, minWidth: "100px" }}>
                            <span
                              style={{
                                fontSize: "11px",
                                fontWeight: "500",
                                color: "#6c757d",
                                marginBottom: "2px",
                                display: "block",
                              }}
                            >
                              شماره سالن
                            </span>
                            <input
                              className="exhibition-input"
                              type="text"
                              value={current.floor_number || ""}
                              onChange={(e) => {
                                const number = e.target.value;
                                const updatedFloor = {
                                  ...current,
                                  floor_number: number,
                                };
                                setExhibitionFloors((prev) =>
                                  prev.map((floor) =>
                                    floor.id === current.id
                                      ? updatedFloor
                                      : floor
                                  )
                                );
                              }}
                              style={{
                                width: "80px",
                                height: "28px",
                                fontSize: "12px",
                                padding: "4px 6px",
                                border: "1px solid #ced4da",
                                borderRadius: "4px",
                              }}
                            />
                          </label>
                          <label style={{ margin: 0, minWidth: "100px" }}>
                            <span
                              style={{
                                fontSize: "11px",
                                fontWeight: "500",
                                color: "#6c757d",
                                marginBottom: "2px",
                                display: "block",
                              }}
                            >
                              رنگ تم
                            </span>
                            <input
                              className="exhibition-input"
                              type="color"
                              value={
                                current.floor_background_color || siteTheme
                              }
                              onChange={(e) => {
                                const color = e.target.value;
                                // Update the current floor data for real-time preview
                                const updatedFloor = {
                                  ...current,
                                  floor_background_color: color,
                                };
                                // Update the exhibitionFloors state for real-time preview
                                setExhibitionFloors((prev) =>
                                  prev.map((floor) =>
                                    floor.id === current.id
                                      ? updatedFloor
                                      : floor
                                  )
                                );
                                // Also update the global theme
                                onChangeTheme(color);
                              }}
                              style={{
                                width: "40px",
                                height: "28px",
                                padding: 0,
                                border: "1px solid #ced4da",
                                borderRadius: "4px",
                              }}
                            />
                          </label>
                          <label style={{ margin: 0, minWidth: "120px" }}>
                            <span
                              style={{
                                fontSize: "11px",
                                fontWeight: "500",
                                color: "#6c757d",
                                marginBottom: "2px",
                                display: "block",
                              }}
                            >
                              تاریخ ایجاد
                            </span>
                            <DatePicker
                              value={
                                current.created_date
                                  ? new DateObject(current.created_date)
                                  : new DateObject({
                                      calendar: persian,
                                      locale: persian_fa,
                                    })
                              }
                              onChange={(date) => {
                                const updatedFloor = {
                                  ...current,
                                  created_date: date
                                    ? date.format("YYYY-MM-DD")
                                    : "",
                                };
                                setExhibitionFloors((prev) =>
                                  prev.map((floor) =>
                                    floor.id === current.id
                                      ? updatedFloor
                                      : floor
                                  )
                                );
                              }}
                              calendar={persian}
                              locale={persian_fa}
                              format="YYYY-MM-DD"
                              style={{
                                width: "110px",
                                height: "28px",
                                border: "1px solid #ced4da",
                                borderRadius: "4px",
                                padding: "4px 6px",
                                fontSize: "12px",
                              }}
                            />
                          </label>
                        </div>
                      )}

                      {/* Map Controls - X, Y, Position */}
                      {current && (
                        <div
                          style={{
                            marginBottom: 12,
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            flexWrap: "wrap",
                          }}
                        >
                          <label
                            className="exhibition-stack"
                            style={{ margin: 0, minWidth: "80px" }}
                          >
                            <span
                              className="exhibition-label"
                              style={{ fontSize: "12px", marginBottom: "4px" }}
                            >
                              X ورودی
                            </span>
                            <input
                              className="exhibition-input"
                              type="number"
                              value={current.entrance_x || 50}
                              onChange={(e) => {
                                const x = Number(e.target.value);
                                const updatedFloor = {
                                  ...current,
                                  entrance_x: x,
                                };
                                setExhibitionFloors((prev) =>
                                  prev.map((floor) =>
                                    floor.id === current.id
                                      ? updatedFloor
                                      : floor
                                  )
                                );
                              }}
                              style={{
                                width: "60px",
                                height: "32px",
                                fontSize: "12px",
                                padding: "4px 8px",
                              }}
                            />
                          </label>
                          <label
                            className="exhibition-stack"
                            style={{ margin: 0, minWidth: "80px" }}
                          >
                            <span
                              className="exhibition-label"
                              style={{ fontSize: "12px", marginBottom: "4px" }}
                            >
                              Y ورودی
                            </span>
                            <input
                              className="exhibition-input"
                              type="number"
                              value={current.entrance_y || 50}
                              onChange={(e) => {
                                const y = Number(e.target.value);
                                const updatedFloor = {
                                  ...current,
                                  entrance_y: y,
                                };
                                setExhibitionFloors((prev) =>
                                  prev.map((floor) =>
                                    floor.id === current.id
                                      ? updatedFloor
                                      : floor
                                  )
                                );
                              }}
                              style={{
                                width: "60px",
                                height: "32px",
                                fontSize: "12px",
                                padding: "4px 8px",
                              }}
                            />
                          </label>
                          <label
                            className="exhibition-stack"
                            style={{ margin: 0, minWidth: "100px" }}
                          >
                            <span
                              className="exhibition-label"
                              style={{ fontSize: "12px", marginBottom: "4px" }}
                            >
                              سمت ورودی
                            </span>
                            <select
                              className="exhibition-select"
                              value={current.entrance_position || "top"}
                              onChange={(e) => {
                                const position = e.target.value;
                                const updatedFloor = {
                                  ...current,
                                  entrance_position: position,
                                };
                                setExhibitionFloors((prev) =>
                                  prev.map((floor) =>
                                    floor.id === current.id
                                      ? updatedFloor
                                      : floor
                                  )
                                );
                              }}
                              style={{
                                width: "80px",
                                height: "32px",
                                fontSize: "12px",
                                padding: "4px 8px",
                              }}
                            >
                              <option value="top">بالا</option>
                              <option value="bottom">پایین</option>
                              <option value="left">چپ</option>
                              <option value="right">راست</option>
                            </select>
                          </label>
                          <button
                            className="exhibition-btn"
                            onClick={updateFloor}
                            style={{
                              backgroundColor: "var(--site-theme)",
                              color: "var(--site-theme-یشقن)",
                              padding: "6px 12px",
                              borderRadius: "6px",
                              border: "none",
                              cursor: "pointer",
                              fontSize: "12px",
                              fontWeight: "600",
                              height: "32px",
                              marginTop: "20px",
                            }}
                          >
                            ذخیره
                          </button>
                        </div>
                      )}

                      {current?.floor_name === "زوفا" ? (
                        <div className="meeting-cards-container">
                          <h3
                            style={{
                              textAlign: "center",
                              marginBottom: "20px",
                              color: "#333",
                            }}
                          >
                            جلسات تخصصی
                          </h3>
                          {isLoadingMeetingCards ? (
                            <div
                              style={{ textAlign: "center", padding: "20px" }}
                            >
                              در حال بارگذاری جلسات...
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns:
                                  "repeat(auto-fill, minmax(300px, 1fr))",
                                gap: "16px",
                                padding: "16px",
                              }}
                            >
                              {meetingCards.map((meeting) => (
                                <div
                                  key={meeting.id}
                                  className="meeting-card"
                                  style={{
                                    backgroundColor:
                                      meeting.background_color || "#f8f9fa",
                                    borderRadius: "12px",
                                    padding: "20px",
                                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                                    border: "1px solid #e9ecef",
                                    transition: "transform 0.2s ease",
                                    cursor: "pointer",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform =
                                      "translateY(-2px)";
                                    e.currentTarget.style.boxShadow =
                                      "0 6px 12px rgba(0, 0, 0, 0.15)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform =
                                      "translateY(0)";
                                    e.currentTarget.style.boxShadow =
                                      "0 4px 6px rgba(0, 0, 0, 0.1)";
                                  }}
                                >
                                  <div style={{ marginBottom: "12px" }}>
                                    <h4
                                      style={{
                                        margin: "0 0 8px 0",
                                        fontSize: "18px",
                                        fontWeight: "bold",
                                        color: "#333",
                                      }}
                                    >
                                      {meeting.title}
                                    </h4>
                                    <p
                                      style={{
                                        margin: "0 0 8px 0",
                                        fontSize: "14px",
                                        color: "#666",
                                        lineHeight: "1.4",
                                      }}
                                    >
                                      {meeting.description}
                                    </p>
                                  </div>

                                  <div style={{ marginBottom: "12px" }}>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          color: "#666",
                                        }}
                                      >
                                        📅
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "14px",
                                          fontWeight: "500",
                                        }}
                                      >
                                        {meeting.date} - {meeting.time}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        marginBottom: "8px",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          color: "#666",
                                        }}
                                      >
                                        👥
                                      </span>
                                      <span style={{ fontSize: "14px" }}>
                                        {meeting.speakers?.length || 0} سخنران
                                      </span>
                                    </div>
                                  </div>

                                  {meeting.speakers &&
                                    meeting.speakers.length > 0 && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#666",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          سخنرانان:
                                        </div>
                                        <div
                                          style={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: "4px",
                                          }}
                                        >
                                          {meeting.speakers.map(
                                            (speaker, index) => (
                                              <span
                                                key={speaker.id}
                                                style={{
                                                  backgroundColor:
                                                    "rgba(255, 255, 255, 0.7)",
                                                  padding: "2px 6px",
                                                  borderRadius: "4px",
                                                  fontSize: "11px",
                                                  color: "#333",
                                                }}
                                              >
                                                {speaker.name}
                                              </span>
                                            )
                                          )}
                                        </div>
                                      </div>
                                    )}

                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginTop: "12px",
                                    }}
                                  >
                                    <span
                                      style={{
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                        fontSize: "11px",
                                        fontWeight: "500",
                                        backgroundColor:
                                          meeting.status === "Underway"
                                            ? "#d4edda"
                                            : "#fff3cd",
                                        color:
                                          meeting.status === "Underway"
                                            ? "#155724"
                                            : "#856404",
                                      }}
                                    >
                                      {meeting.status === "Underway"
                                        ? "در حال برگزاری"
                                        : "در انتظار برگزاری"}
                                    </span>
                                    {meeting.live_broadcast_link && (
                                      <a
                                        href={meeting.live_broadcast_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          padding: "6px 12px",
                                          backgroundColor: "#007bff",
                                          color: "white",
                                          textDecoration: "none",
                                          borderRadius: "4px",
                                          fontSize: "12px",
                                          fontWeight: "500",
                                        }}
                                      >
                                        پخش زنده
                                      </a>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : storesLoading ? (
                        <div style={{ textAlign: "center", padding: "20px" }}>
                          در حال بارگذاری غرفه‌ها...
                        </div>
                      ) : (
                        <div
                          style={{
                            height: "60vh",
                            minHeight: "400px",
                            width: "100%",
                          }}
                        >
                          <FloorMap floor={current} stores={effectiveStores} />
                        </div>
                      )}
                    </div>

                    <aside className="exhibition-card">
                      <div className="exhibition-stack">
                        <input
                          className="exhibition-input"
                          placeholder="جستجو بر اساس شماره غرفه، نام غرفه "
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                        />
                      </div>
                      <div
                        className="exhibition-list exhibition-stack"
                        style={{ marginTop: 12 }}
                      >
                        {filtered.reverse().map(({ s, si }) => (
                          <div
                            key={si}
                            className="exhibition-card exhibition-stack"
                          >
                            <div
                              className="exhibition-row"
                              style={{
                                justifyContent: "center",
                                alignItems: "center",
                                marginBottom: 12,
                              }}
                            >
                              <div
                                className="exhibition-title"
                                style={{ fontSize: 16, margin: 0 }}
                              >
                                غرفه{" "}
                                {toPersianDigits(s.store_id_number ?? s.id)}{" "}
                                {s.__draft ? "(پیش‌نویس)" : ""}
                              </div>
                            </div>

                            <div className="exhibition-grid exhibition-grid-2">
                              <label className="exhibition-stack">
                                <span className="exhibition-label">
                                  نام غرفه
                                </span>
                                <input
                                  className="exhibition-input"
                                  value={s.store_name || ""}
                                  onChange={(e) =>
                                    updateStore(si, {
                                      store_name: e.target.value,
                                    })
                                  }
                                />
                              </label>
                              <label className="exhibition-stack">
                                <span className="exhibition-label">
                                  شماره غرفه
                                </span>
                                <input
                                  className="exhibition-input"
                                  type="number"
                                  value={s.store_id_number ?? ""}
                                  onChange={(e) =>
                                    updateStore(si, {
                                      store_id_number: Number(e.target.value),
                                    })
                                  }
                                />
                              </label>
                              <label className="exhibition-stack">
                                <span className="exhibition-label">X</span>
                                <input
                                  className="exhibition-input"
                                  type="number"
                                  value={
                                    typeof s.start_x !== "undefined"
                                      ? s.start_x
                                      : s.x || 0
                                  }
                                  onChange={(e) =>
                                    updateStore(si, {
                                      start_x: Number(e.target.value),
                                    })
                                  }
                                />
                              </label>
                              <label className="exhibition-stack">
                                <span className="exhibition-label">Y</span>
                                <input
                                  className="exhibition-input"
                                  type="number"
                                  value={
                                    typeof s.start_y !== "undefined"
                                      ? s.start_y
                                      : s.y || 0
                                  }
                                  onChange={(e) =>
                                    updateStore(si, {
                                      start_y: Number(e.target.value),
                                    })
                                  }
                                />
                              </label>
                              <label className="exhibition-stack">
                                <span className="exhibition-label">عرض</span>
                                <input
                                  className="exhibition-input"
                                  type="number"
                                  value={s.width}
                                  onChange={(e) =>
                                    updateStore(si, {
                                      width: Number(e.target.value),
                                    })
                                  }
                                />
                              </label>
                              <label className="exhibition-stack">
                                <span className="exhibition-label">ارتفاع</span>
                                <input
                                  className="exhibition-input"
                                  type="number"
                                  value={s.height}
                                  onChange={(e) =>
                                    updateStore(si, {
                                      height: Number(e.target.value),
                                    })
                                  }
                                />
                              </label>
                              <label
                                className="exhibition-stack"
                                style={{ gridColumn: "1 / -1" }}
                              >
                                <span className="exhibition-label">
                                  سمت ورودی غرفه
                                </span>
                                <select
                                  className="exhibition-select"
                                  value={s.entrance}
                                  onChange={(e) =>
                                    updateStore(si, {
                                      entrance: e.target.value,
                                    })
                                  }
                                >
                                  <option value="top">بالا</option>
                                  <option value="bottom">پایین</option>
                                  <option value="left">چپ</option>
                                  <option value="right">راست</option>
                                </select>
                              </label>
                            </div>

                            <div className="exhibition-stack">
                              <label className="exhibition-stack">
                                <span className="exhibition-label">
                                  شماره تماس
                                </span>
                                <input
                                  className="exhibition-input"
                                  value={s.phone_number || ""}
                                  onChange={(e) =>
                                    updateStore(si, {
                                      phone_number: e.target.value,
                                    })
                                  }
                                />
                              </label>
                              <label className="exhibition-stack">
                                <span className="exhibition-label">
                                  توضیحات
                                </span>
                                <textarea
                                  className="exhibition-textarea"
                                  rows="3"
                                  value={s.description || ""}
                                  onChange={(e) =>
                                    updateStore(si, {
                                      description: e.target.value,
                                    })
                                  }
                                />
                              </label>
                              <label className="exhibition-stack">
                                <span className="exhibition-label">
                                  محصولات
                                </span>
                                <input
                                  className="exhibition-input"
                                  value={s.products || ""}
                                  onChange={(e) =>
                                    updateStore(si, {
                                      products: e.target.value,
                                    })
                                  }
                                />
                              </label>
                              <label className="exhibition-stack">
                                <span className="exhibition-label">
                                  آدرس کانال وقف نما
                                </span>
                                <input
                                  className="exhibition-input"
                                  value={s.vaghfnama_channel_address || ""}
                                  onChange={(e) =>
                                    updateStore(si, {
                                      vaghfnama_channel_address: e.target.value,
                                    })
                                  }
                                />
                              </label>
                              <label className="exhibition-stack">
                                <span className="exhibition-label">
                                  آدرس سایت
                                </span>
                                <input
                                  className="exhibition-input"
                                  value={s.site_address || ""}
                                  onChange={(e) =>
                                    updateStore(si, {
                                      site_address: e.target.value,
                                    })
                                  }
                                />
                              </label>
                            </div>

                            {/* Cover upload section - available for both drafts and existing stores */}
                            <div className="exhibition-stack">
                              <label className="exhibition-stack">
                                <span className="exhibition-label">کاور</span>
                                <input
                                  className="exhibition-input"
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file =
                                      e.target.files && e.target.files[0]
                                        ? e.target.files[0]
                                        : null;
                                    handleCoverChange(s.id, file);
                                    updateStore(si, { cover: file });
                                  }}
                                />
                              </label>
                              {coverPreviews[s.id] && (
                                <div style={{ marginTop: "8px" }}>
                                  <span
                                    className="exhibition-label"
                                    style={{
                                      fontSize: "14px",
                                      marginBottom: "4px",
                                      display: "block",
                                    }}
                                  >
                                    پیش‌نمایش کاور:
                                  </span>
                                  <img
                                    src={coverPreviews[s.id]}
                                    alt="Cover preview"
                                    style={{
                                      width: "100%",
                                      maxWidth: "200px",
                                      height: "120px",
                                      objectFit: "cover",
                                      borderRadius: "8px",
                                      border: "1px solid #ddd",
                                    }}
                                  />
                                </div>
                              )}
                              {/* Show current cover for existing stores */}
                              {!s.__draft &&
                                s.cover &&
                                !coverPreviews[s.id] && (
                                  <div style={{ marginTop: "8px" }}>
                                    <span
                                      className="exhibition-label"
                                      style={{
                                        fontSize: "14px",
                                        marginBottom: "4px",
                                        display: "block",
                                      }}
                                    >
                                      کاور فعلی:
                                    </span>
                                    <img
                                      src={s.cover}
                                      alt="Current cover"
                                      style={{
                                        width: "100%",
                                        maxWidth: "200px",
                                        height: "120px",
                                        objectFit: "cover",
                                        borderRadius: "8px",
                                        border: "1px solid #ddd",
                                      }}
                                    />
                                  </div>
                                )}
                            </div>

                            {s.__draft && (
                              <div className="exhibition-stack">
                                <label className="exhibition-stack">
                                  <span className="exhibition-label">
                                    تاریخ ایجاد
                                  </span>
                                  <DatePicker
                                    value={s.created_date}
                                    onChange={(date) =>
                                      updateStore(si, { created_date: date })
                                    }
                                    calendar={persian}
                                    locale={persian_fa}
                                    format="YYYY/MM/DD"
                                    style={{
                                      border: "1px solid #ccc",
                                      borderRadius: "5px",
                                      padding: "8px",
                                      width: "100%",
                                    }}
                                  />
                                </label>
                              </div>
                            )}

                            {/* Action Buttons */}
                            <div
                              className="exhibition-row"
                              style={{
                                gap: 8,
                                justifyContent: "center",
                                marginTop: 12,
                              }}
                            >
                              {s.__draft ? (
                                <>
                                  <button
                                    className="exhibition-btn"
                                    onClick={() => saveDraftStore(s)}
                                    disabled={isSubmittingStore}
                                  >
                                    {isSubmittingStore
                                      ? "در حال ذخیره..."
                                      : "ذخیره"}
                                  </button>
                                  <button
                                    className="exhibition-btn"
                                    style={{
                                      background: "#fee2e2",
                                      color: "#991b1b",
                                    }}
                                    onClick={() => removeDraftStore(s.id)}
                                    disabled={isSubmittingStore}
                                  >
                                    انصراف
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    className="exhibition-btn"
                                    onClick={async () => {
                                      setIsSubmittingStore(true);
                                      try {
                                        // Update store body first
                                        await submitUpdateStoreBody(s);
                                        // Then update cover if a new one was selected
                                        if (s.cover && coverPreviews[s.id]) {
                                          await submitUpdateStoreCover(s);
                                        }
                                      } finally {
                                        setIsSubmittingStore(false);
                                      }
                                    }}
                                    disabled={isSubmittingStore}
                                  >
                                    {isSubmittingStore
                                      ? "در حال ذخیره..."
                                      : "ذخیره"}
                                  </button>
                                  <button
                                    className="exhibition-btn"
                                    style={{
                                      background: "#fee2e2",
                                      color: "#991b1b",
                                    }}
                                    onClick={() => submitDeleteStore(s.id)}
                                    disabled={isSubmittingStore || deletingStoreId === s.id}
                                  >
                                    {deletingStoreId === s.id ? "در حال حذف..." : "حذف"}
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </aside>
                  </div>
                </div>
              </Row>
            </Container>
          </section>
        </Container>
        <ThinFooter />
      </ContentWrapper>

      {/* Exhibition Creation Modal */}
      {showExhibitionForm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowExhibitionForm(false)}
        >
          <div
            className="exhibition-card"
            style={{
              width: "90%",
              maxWidth: "600px",
              maxHeight: "90vh",
              overflow: "auto",
              margin: "20px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="exhibition-title"
              style={{ marginBottom: 20, textAlign: "center" }}
            >
              ایجاد نمایشگاه جدید
            </div>

            <div
              className="exhibition-grid exhibition-grid-2"
              style={{ marginBottom: 20, textAlign: "right" }}
            >
              <label className="exhibition-stack">
                <span className="exhibition-label">نام نمایشگاه</span>
                <input
                  className="exhibition-input"
                  type="text"
                  value={exhibitionForm.exhibition_name}
                  onChange={(e) =>
                    handleExhibitionFormChange(
                      "exhibition_name",
                      e.target.value
                    )
                  }
                  placeholder="نام نمایشگاه را وارد کنید"
                />
              </label>

              <label className="exhibition-stack">
                <span className="exhibition-label">سال و ماه نمایشگاه</span>
                <input
                  className="exhibition-input"
                  type="text"
                  value={exhibitionForm.exhibition_year}
                  onChange={(e) =>
                    handleExhibitionFormChange(
                      "exhibition_year",
                      e.target.value
                    )
                  }
                  placeholder="شهریور 1404"
                />
              </label>

              <label className="exhibition-stack">
                <span className="exhibition-label">مکان نمایشگاه</span>
                <input
                  className="exhibition-input"
                  type="text"
                  value={exhibitionForm.exhibition_place}
                  onChange={(e) =>
                    handleExhibitionFormChange(
                      "exhibition_place",
                      e.target.value
                    )
                  }
                  placeholder="مکان نمایشگاه را وارد کنید"
                />
              </label>

              <label className="exhibition-stack">
                <span className="exhibition-label">تاریخ ایجاد</span>
                <DatePicker
                  value={exhibitionForm.created_date}
                  onChange={(date) =>
                    handleExhibitionFormChange("created_date", date)
                  }
                  calendar={persian}
                  locale={persian_fa}
                  format="YYYY/MM/DD"
                  style={{
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                    padding: "8px",
                    width: "100%",
                  }}
                />
              </label>
            </div>

            <div
              className="exhibition-row"
              style={{ gap: 12, justifyContent: "left" }}
            >
              <button
                className="exhibition-btn"
                style={{
                  background: "#fee2e2",
                  color: "#991b1b",
                  padding: "10px 20px",
                }}
                onClick={() => setShowExhibitionForm(false)}
                disabled={isSubmitting}
              >
                انصراف
              </button>
              <button
                className="exhibition-btn"
                style={{
                  padding: "10px 20px",
                }}
                onClick={addExhibition}
                disabled={isSubmitting}
              >
                {isSubmitting ? "در حال ایجاد..." : "ایجاد نمایشگاه"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floor Creation Modal - REMOVED */}
      {false && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowFloorForm(false)}
        >
          <div
            className="exhibition-card"
            style={{
              width: "90%",
              maxWidth: "600px",
              maxHeight: "90vh",
              overflow: "auto",
              margin: "20px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="exhibition-title"
              style={{ marginBottom: 20, textAlign: "center" }}
            >
              ایجاد سالن جدید
            </div>

            <div
              className="exhibition-grid exhibition-grid-2"
              style={{ marginBottom: 20, textAlign: "right" }}
            >
              <label className="exhibition-stack">
                <span className="exhibition-label">نمایشگاه</span>
                <select
                  className="exhibition-select"
                  value={selectedExhibitionId || ""}
                  onChange={(e) => setSelectedExhibitionId(e.target.value)}
                  disabled
                >
                  <option value="">انتخاب نمایشگاه</option>
                  {exhibitions?.results?.map((exh) => (
                    <option key={exh.id} value={exh.id}>
                      {exh.exhibition_name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="exhibition-stack">
                <span className="exhibition-label">نام سالن</span>
                <input
                  className="exhibition-input"
                  type="text"
                  value={floorForm.floor_name}
                  onChange={(e) =>
                    handleFloorFormChange("floor_name", e.target.value)
                  }
                  placeholder="مثلاً سالن ۱"
                />
              </label>

              <label className="exhibition-stack">
                <span className="exhibition-label">شماره سالن</span>
                <input
                  className="exhibition-input"
                  type="number"
                  value={floorForm.floor_number}
                  onChange={(e) =>
                    handleFloorFormChange("floor_number", e.target.value)
                  }
                  placeholder="مثلاً 1"
                />
              </label>

              <label className="exhibition-stack">
                <span className="exhibition-label">رنگ پسزمینه سالن</span>
                <input
                  className="exhibition-input"
                  type="color"
                  value={floorForm.floor_background_color}
                  onChange={(e) =>
                    handleFloorFormChange(
                      "floor_background_color",
                      e.target.value
                    )
                  }
                  style={{ width: 60, height: 40, padding: 0 }}
                />
              </label>

              <label className="exhibition-stack">
                <span className="exhibition-label">X ورودی سالن</span>
                <input
                  className="exhibition-input"
                  type="number"
                  value={floorForm.entrance_x}
                  onChange={(e) =>
                    handleFloorFormChange("entrance_x", e.target.value)
                  }
                />
              </label>

              <label className="exhibition-stack">
                <span className="exhibition-label">Y ورودی سالن</span>
                <input
                  className="exhibition-input"
                  type="number"
                  value={floorForm.entrance_y}
                  onChange={(e) =>
                    handleFloorFormChange("entrance_y", e.target.value)
                  }
                />
              </label>

              <label
                className="exhibition-stack"
                style={{ gridColumn: "1 / -1" }}
              >
                <span className="exhibition-label">سمت ورودی سالن</span>
                <select
                  className="exhibition-select"
                  value={floorForm.entrance_position}
                  onChange={(e) =>
                    handleFloorFormChange("entrance_position", e.target.value)
                  }
                >
                  <option value="top">بالا</option>
                  <option value="bottom">پایین</option>
                  <option value="left">چپ</option>
                  <option value="right">راست</option>
                </select>
              </label>

              <label className="exhibition-stack">
                <span className="exhibition-label">تاریخ ایجاد</span>
                <DatePicker
                  value={floorForm.created_date}
                  onChange={(date) =>
                    handleFloorFormChange("created_date", date)
                  }
                  calendar={persian}
                  locale={persian_fa}
                  format="YYYY/MM/DD"
                  style={{
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                    padding: "8px",
                    width: "100%",
                  }}
                />
              </label>
            </div>

            <div
              className="exhibition-row"
              style={{ gap: 12, justifyContent: "left" }}
            >
              <button
                className="exhibition-btn"
                style={{
                  background: "#fee2e2",
                  color: "#991b1b",
                  padding: "10px 20px",
                }}
                onClick={() => setShowFloorForm(false)}
                disabled={isSubmittingFloor}
              >
                انصراف
              </button>
              <button
                className="exhibition-btn"
                style={{
                  padding: "10px 20px",
                }}
                onClick={submitFloor}
                disabled={isSubmittingFloor}
              >
                {isSubmittingFloor ? "در حال ایجاد..." : "ایجاد سالن"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Admin;
